/**
 * CreateEventTickets — Step: Tickets.
 *
 * Ticket types and ticket groups are authored once in the Tickets section
 * (left rail). This step no longer CREATES tickets — it lets you SELECT which
 * preset ticket types and ticket groups apply to this event.
 *
 * Selections are stored as id arrays (ticketTypeIds / ticketGroupIds) and
 * flattened into cfg.tickets + cfg.ticketGroups so the Publish / Live steps
 * (which read cfg.tickets) keep working verbatim.
 */

import { alpha, Btn, Card, Dropdown, Icon, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { useNav } from "../../../shell/router";
import { CreationShell } from "./CreationShell";
import { CreationStore, useCreationConfig } from "./store";
import { FieldError, WizardCanvas } from "./wizard-utils";
import { useTicketTypes, TT_KINDS, type TicketType } from "../../tickets/ticket-types-store";
import { useTicketGroups, tgPriceLabel, tgFlattenToTickets, type TicketGroup } from "../../tickets/ticket-groups-store";
import { NumberField } from "../../../lib/NumberField";
import { useT } from "../../../lib/i18n";

function SelectableCard({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Btn variant="ghost" type="button" onClick={onClick} style={{ display: "flex", gap: 12, padding: "14px 16px", border: `${selected ? 2 : 1}px solid ${selected ? T.p1 : T.n4}`, borderRadius: 10, background: selected ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left", width: "100%", height: "auto", alignItems: "flex-start", whiteSpace: "normal", transition: "background 120ms, border-color 120ms" }}>
      <span style={{ width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${selected ? T.p1 : T.n4}`, background: selected ? T.p1 : T.white, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>{selected ? <Icon.check size={13} /> : null}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </Btn>
  );
}

function EmptyLibraryNote({ label, onManage }: { label: string; onManage: () => void }) {
  const t = useT();
  return (
    <div style={{ padding: 22, border: `1px dashed ${T.n4}`, borderRadius: 10, textAlign: "center", display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
      <span style={{ fontSize: 13, color: T.soft }}>{t("events.creationCreateEventTickets.no", "No")}{" "}{label} {t("events.creationCreateEventTickets.inYourLibraryYet", "in your library yet.")}</span>
      <Btn variant="secondary" size="sm" icon={<Icon.external size={13} />} onClick={onManage}>{t("common.actions.manage", "Manage")}{" "}{label}</Btn>
    </div>
  );
}

/* Per-event override applied to a selected ticket type. Every type starts at
 * zero tickets; the coordinator picks the kind and, when paid/donation, sets
 * the price — UNLESS an admin has locked the price on the ticket type, in which
 * case the locked price is forced and the controls are read-only. */
type TypeCfg = { qty: number; kind: TicketType["kind"]; price: number };
const cfgFor = (t: TicketType): TypeCfg => t.priceLocked
  ? { qty: 0, kind: "paid", price: t.price }
  : { qty: 0, kind: t.kind ?? "free", price: 0 };
const isPricedKind = (k: TicketType["kind"]) => k === "paid" || k === "donation";

function TicketTypeCard({ t, selected, cfg, onToggle, onConfig }: { t: TicketType; selected: boolean; cfg: TypeCfg; onToggle: () => void; onConfig: (patch: Partial<TypeCfg>) => void }) {
  const translate = useT();
  const locked = !!t.priceLocked;
  const kind = locked ? "paid" : cfg.kind;
  const priced = isPricedKind(kind);
  // Pill reflects the effective pricing.
  const pillTone: "success" | "warn" | "neutral" = kind === "donation" ? "warn" : priced ? "success" : "neutral";
  const pillLabel = locked ? `$${(t.price || 0).toLocaleString()}` : priced ? `$${(cfg.price || 0).toLocaleString()}` : "Free";
  const fieldLabel: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, color: T.n1 };
  return (
    <div style={{ border: `${selected ? 2 : 1}px solid ${selected ? T.p1 : T.n4}`, borderRadius: 10, background: selected ? T.p4 : T.white, overflow: "hidden", transition: "background 120ms, border-color 120ms" }}>
      <Btn variant="ghost" type="button" onClick={onToggle} style={{ display: "flex", gap: 12, padding: "14px 16px", border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left", width: "100%", height: "auto", alignItems: "flex-start", whiteSpace: "normal" }}>
        <span style={{ width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${selected ? T.p1 : T.n4}`, background: selected ? T.p1 : T.white, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>{selected ? <Icon.check size={13} /> : null}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: t.color, flexShrink: 0 }} />
            <span style={{ fontSize: 14.5, fontWeight: 700, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name || translate("events.createEventTickets.untitledType", "Untitled type")}</span>
            <Pill tone={pillTone}>{locked ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Icon.lock size={10} /> {pillLabel}</span> : pillLabel}</Pill>
          </span>
          {t.desc ? <span style={{ display: "block", fontSize: 12, color: T.soft, marginTop: 5, lineHeight: 1.4 }}>{t.desc}</span> : null}
        </span>
      </Btn>
      {selected ? (
        <div onClick={(e) => e.stopPropagation()} style={{ padding: "12px 16px 16px", borderTop: `1px solid ${alpha(T.p1, "22")}`, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span style={fieldLabel}>{translate("events.creationCreateEventTickets.tickets", "Tickets")}</span>
            <NumberField value={cfg.qty} onChange={(n) => onConfig({ qty: n })} ariaLabel={translate('events.creationCreateEventTickets.ariaLabel')} style={{ width: 88 }} />
          </div>
          {locked ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={fieldLabel}>{translate("events.creationCreateEventTickets.pricing", "Pricing")}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: T.n1 }}><Icon.lock size={13} /> ${(t.price || 0).toLocaleString()} {translate("events.creationCreateEventTickets.lockedByAdmin", "· locked by admin")}</span>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <span style={fieldLabel}>{translate("events.creationCreateEventTickets.kind", "Kind")}</span>
                <Dropdown
                  options={TT_KINDS.map((k) => ({ value: k.id, label: k.label }))}
                  value={cfg.kind}
                  onChange={(v) => onConfig({ kind: v as TicketType["kind"] })}
                  aria-label={translate("events.creationCreateEventTickets.kind", "Kind")}
                  portal
                  style={{ gap: 0, width: 180, flexShrink: 0 }}
                />
              </div>
              {priced ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <span style={fieldLabel}>{translate("events.creationCreateEventTickets.price", "Price")}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14, color: T.soft }}>$</span>
                    <NumberField value={cfg.price} onChange={(n) => onConfig({ price: n })} decimal ariaLabel={translate('events.creationCreateEventTickets.ariaLabel2')} style={{ width: 88 }} />
                  </span>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function CreateEventTickets() {
  const t = useT();
  const cfg = useCreationConfig();
  const nav = useNav();
  const types = useTicketTypes();
  const groups = useTicketGroups();

  const typeIds = (cfg.ticketTypeIds as string[] | undefined) || [];
  const groupIds = (cfg.ticketGroupIds as string[] | undefined) || [];
  const typeConfigs = (cfg.ticketTypeConfigs as Record<string, TypeCfg> | undefined) || {};

  const commit = (nextTypeIds: string[], nextGroupIds: string[], nextConfigs: Record<string, TypeCfg>) => {
    const selTypes = types.filter((t) => nextTypeIds.includes(t.id));
    const selGroups = groups.filter((g) => nextGroupIds.includes(g.id));
    const typeRows = selTypes.map((t) => {
      const c = nextConfigs[t.id] || cfgFor(t);
      const locked = !!t.priceLocked;
      const kind = (locked ? "paid" : c.kind) as TicketType["kind"];
      const priced = isPricedKind(kind);
      const price = locked ? t.price : (priced ? c.price : 0);
      return { id: t.id, name: t.name, price, kind, color: t.color, capacity: c.qty, sold: 0, source: "type" as const };
    });
    const groupRows = tgFlattenToTickets(selGroups).map((r) => ({ ...r, sold: 0, source: "group" as const }));
    const allRows = [...typeRows, ...groupRows];
    // Event capacity is the sum of every issued ticket quantity across the
    // selected types + groups. Empty (null) until at least one quantity is set,
    // then surfaced as the event capacity everywhere (Publish, list, adapter).
    const totalCapacity = allRows.reduce((sum, r) => sum + (Number((r as { capacity?: number }).capacity) > 0 ? Number((r as { capacity?: number }).capacity) : 0), 0);
    CreationStore.setField("ticketTypeIds", nextTypeIds);
    CreationStore.setField("ticketGroupIds", nextGroupIds);
    CreationStore.setField("ticketTypeConfigs", nextConfigs);
    CreationStore.setField("ticketGroups", selGroups);
    CreationStore.setField("tickets", allRows);
    CreationStore.setField("capacity", totalCapacity > 0 ? totalCapacity : null);
  };

  const toggleType = (id: string) => {
    if (typeIds.includes(id)) {
      const nextConfigs = { ...typeConfigs }; delete nextConfigs[id];
      commit(typeIds.filter((x) => x !== id), groupIds, nextConfigs);
    } else {
      const t = types.find((x) => x.id === id);
      commit([...typeIds, id], groupIds, { ...typeConfigs, [id]: t ? cfgFor(t) : { qty: 0, kind: "free", price: 0 } });
    }
  };
  const setTypeConfig = (id: string, patch: Partial<TypeCfg>) => {
    const t = types.find((x) => x.id === id);
    const cur = typeConfigs[id] || (t ? cfgFor(t) : { qty: 0, kind: "free", price: 0 });
    commit(typeIds, groupIds, { ...typeConfigs, [id]: { ...cur, ...patch } });
  };
  const toggleGroup = (id: string) => commit(typeIds, groupIds.includes(id) ? groupIds.filter((x) => x !== id) : [...groupIds, id], typeConfigs);

  const selectedCount = typeIds.length + groupIds.length;
  const nextDisabled = selectedCount === 0;

  // Event capacity = sum of every issued ticket quantity for this event.
  const capacityTotal = ((cfg.tickets as Array<{ capacity?: number }> | undefined) || [])
    .reduce((sum, r) => sum + (Number(r.capacity) > 0 ? Number(r.capacity) : 0), 0);

  return (
    <CreationShell step={3} nextDisabled={nextDisabled} nextDisabledReason={nextDisabled ? "Select at least one ticket type or group" : ""}>
      <WizardCanvas width="wide" cols="stack">
        <div style={{ padding: "12px 16px", background: T.p4, border: `1px solid ${alpha(T.p1, "22")}`, borderRadius: 10, display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: T.n2, lineHeight: 1.5 }}>
          <Icon.info size={15} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>{t("events.creationCreateEventTickets.ticketTypesAndGroupsAreManagedInThe", "Ticket types and groups are managed in the")}{" "}<b style={{ color: T.n1 }}>{t("events.creationCreateEventTickets.tickets", "Tickets")}</b> {t("events.creationCreateEventTickets.sectionSelectThePresetsThatApplyToThisEventEditsToA", "section. Select the presets that apply to this event — edits to a preset flow through to every event that uses it.")}{" "}<b style={{ color: T.n1 }}>{t("events.creationCreateEventTickets.atLeastOneTicketTypeOrGroupIsRequired", "At least one ticket type or group is required")}<span style={{ color: T.red1 }}>*</span></b>.</span>
        </div>
        <FieldError path={["tickets", "ticketTypeIds", "ticketTypeConfigs", "capacity", "waitlist"]} style={{ marginTop: 0 }} />

        {/* ── Ticket types ─────────────────────────────────────────────── */}
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("events.creationCreateEventTickets.ticketTypes", "Ticket types")}</h3>
              <p style={{ ...tx.help, marginTop: 4 }}>{t("events.creationCreateEventTickets.chooseIndividualTicketTypesForThisEvent", "Choose individual ticket types for this event.")}</p>
            </div>
            <Btn variant="ghost" size="sm" icon={<Icon.external size={13} />} onClick={() => nav.go("ticket-types")}>{t("events.creationCreateEventTickets.manageTypes", "Manage types")}</Btn>
          </div>
          {types.length === 0 ? (
            <EmptyLibraryNote label={t("events.creationCreateEventTickets.ticketTypes225360c0", "ticket types")} onManage={() => nav.go("ticket-types")} />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, alignItems: "start" }}>
              {types.map((t: TicketType) => (
                <TicketTypeCard
                  key={t.id}
                  t={t}
                  selected={typeIds.includes(t.id)}
                  cfg={typeConfigs[t.id] || cfgFor(t)}
                  onToggle={() => toggleType(t.id)}
                  onConfig={(patch) => setTypeConfig(t.id, patch)}
                />
              ))}
            </div>
          )}
        </Card>

        {/* ── Ticket groups ────────────────────────────────────────────── */}
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("events.creationCreateEventTickets.ticketGroups", "Ticket groups")}</h3>
              <p style={{ ...tx.help, marginTop: 4 }}>{t("events.creationCreateEventTickets.applyASavedBundleOfTiersCommsFlowsInOneGo", "Apply a saved bundle of tiers + comms flows in one go.")}</p>
            </div>
            <Btn variant="ghost" size="sm" icon={<Icon.external size={13} />} onClick={() => nav.go("ticket-groups")}>{t("events.creationCreateEventTickets.manageGroups", "Manage groups")}</Btn>
          </div>
          {groups.length === 0 ? (
            <EmptyLibraryNote label={t("events.creationCreateEventTickets.ticketGroups5b7ddcf4", "ticket groups")} onManage={() => nav.go("ticket-groups")} />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {groups.map((g: TicketGroup) => {
                const on = groupIds.includes(g.id);
                return (
                  <SelectableCard key={g.id} selected={on} onClick={() => toggleGroup(g.id)}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ display: "inline-flex", width: 26, height: 5, borderRadius: 999, overflow: "hidden", flexShrink: 0 }}>{g.tiers.map((ti) => <span key={ti.id} style={{ flex: 1, background: ti.color }} />)}</span>
                      <span style={{ fontSize: 14.5, fontWeight: 700, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.name || "Untitled group"}</span>
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, fontSize: 12.5, color: T.soft }}>
                      <b style={{ color: T.n1 }}>{tgPriceLabel(g)}</b>
                      <span>· {g.tiers.length} {t("events.creationCreateEventTickets.tier", "tier")}{" "}{g.tiers.length === 1 ? "" : t('events.creationCreateEventTickets.text')}</span>
                    </span>
                    {g.description ? <span style={{ display: "block", fontSize: 12, color: T.soft, marginTop: 4, lineHeight: 1.4 }}>{g.description}</span> : null}
                  </SelectableCard>
                );
              })}
            </div>
          )}
        </Card>

        {selectedCount > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between", padding: "14px 16px", background: T.white, border: `1px solid ${T.n4}`, borderRadius: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.n2 }}>
              <Icon.check size={14} style={{ color: T.green1 }} />
              <span><b style={{ color: T.n1 }}>{selectedCount}</b> {t("events.creationCreateEventTickets.selectedForThisEvent", "selected for this event —")}{" "}{typeIds.length} {t("events.creationCreateEventTickets.type", "type")}{" "}{typeIds.length === 1 ? "" : t('events.creationCreateEventTickets.text')}, {groupIds.length} {t("events.creationCreateEventTickets.group", "group")}{" "}{groupIds.length === 1 ? "" : t('events.creationCreateEventTickets.text')}.</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.03 }}>{t("events.creationCreateEventTickets.eventCapacity", "Event capacity")}</span>
              {capacityTotal > 0
                ? <Pill tone="primary"><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.users size={11} /> {capacityTotal.toLocaleString()} {t("events.creationCreateEventTickets.seats", "seats")}</span></Pill>
                : <span style={{ fontSize: 12.5, color: T.soft }}>{t("events.creationCreateEventTickets.setTicketQuantitiesToDefineCapacity", "Set ticket quantities to define capacity")}</span>}
            </div>
          </div>
        ) : null}
      </WizardCanvas>
    </CreationShell>
  );
}
